import { useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import SettingsIcon from '@mui/icons-material/esm/Settings';
import dailyRecordScaffoldService, { DailyRecordScaffoldResult } from '../../services/api/dailyRecordScaffoldService';

interface DailyRecordScaffoldPanelProps {
  projectId?: string;
  defaultDate: string;
}

export default function DailyRecordScaffoldPanel({ projectId, defaultDate }: DailyRecordScaffoldPanelProps) {
  const [recordDate, setRecordDate] = useState(defaultDate);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<DailyRecordScaffoldResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setRecordDate(defaultDate);
  }, [defaultDate]);

  const run = async () => {
    if (!projectId || !recordDate) return;
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      setResult(await dailyRecordScaffoldService.scaffold(projectId, recordDate));
    } catch (err: any) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to scaffold daily records');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardContent>
        <Stack spacing={2}>
          <Box>
            <Typography variant="h6">Daily Record Scaffold</Typography>
            <Typography variant="body2" color="text.secondary">
              Draft DFR, timesheet, and inspection records from dispatch.
            </Typography>
          </Box>

          {!projectId && <Alert severity="info">Select a project before scaffolding daily records.</Alert>}
          {error && <Alert severity="error">{error}</Alert>}
          {result && (
            <Alert severity="success">
              {result.totals.teams} team(s), {result.totals.dispatchTasks} dispatched task(s), {result.totals.workers} worker(s), {result.totals.inspections} inspection(s).
            </Alert>
          )}

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <TextField
              type="date"
              label="Record Date"
              value={recordDate}
              onChange={(event) => setRecordDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <Button
              variant="contained"
              startIcon={<SettingsIcon />}
              onClick={run}
              disabled={!projectId || !recordDate || loading}
              sx={{ minWidth: 150 }}
            >
              Scaffold
            </Button>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}
